import { FC, useMemo, useState } from 'react';
import { type MenuInfo } from 'rc-menu/lib/interface';

import { Radio } from '@dynamika-shared/ui/Radio/Radio';
import { Dropdown, MenuItemType } from '@dynamika-shared/ui/Dropdown/Dropdown';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { Filter } from '@dynamika-shared/ui/icons';
import { twMerge } from '@dynamika-shared/utils/twMerge';

export type SiteRecipientsSiteFilterOption = {
  id: string;
  code: string;
};

export type SiteRecipientsSiteFilterProps = {
  sites: SiteRecipientsSiteFilterOption[];
  selectedSiteId?: string;
  onSelectedSiteChange: (siteId?: string) => void;
  disabled?: boolean;
};

const SITES_GROUP_KEY = 'site-recipients-sites-group';
const RESET_GROUP_KEY = 'site-recipients-sites-reset';
const SITES_HINT_KEY = 'site-recipients-sites-hint';

/**
 * Seven site rows tall, then it scrolls
 */
const SITES_SCROLL_CLASS =
  '[&_.ant-dropdown-menu-item-group-list]:max-h-[29rem] [&_.ant-dropdown-menu-item-group-list]:overflow-y-auto';

/**
 * Site picker for the Recipients tab — a chip opening a radio list of the
 * trial's sites.
 */
export const SiteRecipientsSiteFilter: FC<SiteRecipientsSiteFilterProps> = ({
  sites,
  selectedSiteId,
  onSelectedSiteChange,
  disabled,
}) => {
  const [visible, setVisible] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');

  const hasSelection = Boolean(selectedSiteId);

  // Ascending site identifier order, per the FRS.
  const sortedSites = useMemo(
    () => [...sites].sort((a, b) => a.code.localeCompare(b.code)),
    [sites]
  );

  const selectedSiteCode = useMemo(
    () => sortedSites.find(site => site.id === selectedSiteId)?.code,
    [sortedSites, selectedSiteId]
  );

  // Partial, case-insensitive match on the site code — the only text a row
  // carries.
  const matchingSites = useMemo(() => {
    const term = siteSearch.trim().toLowerCase();

    if (!term) return sortedSites;

    return sortedSites.filter(site => site.code.toLowerCase().includes(term));
  }, [sortedSites, siteSearch]);

  // The search term belongs to one visit to the menu — a stale one would
  // reopen the list already narrowed, with no visible cause.
  const closeMenu = () => {
    setVisible(false);
    setSiteSearch('');
  };

  /**
   * The site rows — or the one row that says there are none.
   *
   * The empty row is `disabled`: `onMenuClick` would ignore its key anyway, but
   * disabling it also stops antd rendering it as hoverable and focusable.
   */
  const siteMenuRows: MenuItemType[] = useMemo(() => {
    if (!matchingSites.length) {
      return [
        {
          key: SITES_HINT_KEY,
          disabled: true,
          label: (
            <div className='text-1424 text-spanishGray'>
              No sites match your search.
            </div>
          ),
        },
      ];
    }

    return matchingSites.map(site => ({
      key: site.id,
      label: (
        <div className='flex items-center justify-between gap-1'>
          <CustomTooltip
            label={site.code}
            MAX_LABEL_LENGTH={18}
            className='text-1424 text-black font-medium'
          />

          <Radio checked={site.id === selectedSiteId} />
        </div>
      ),
    }));
  }, [matchingSites, selectedSiteId]);

  const menuItems: MenuItemType[] = useMemo(
    () => [
      {
        key: SITES_GROUP_KEY,
        type: 'group' as const,
        label: (
          <div className='flex flex-col gap-1'>
            <div className='text-1216 text-spanishGray'>All Sites</div>

            <Input
              name='siteRecipientsSiteSearch'
              data-testid='siteRecipientsSiteSearch'
              classNameContainer=''
              className='!rounded-[3.2rem] px-2 !py-0'
              placeholder='Search sites'
              allowClear
              value={siteSearch}
              onChange={event => setSiteSearch(event.target.value)}
              /**
               * The menu owns the keyboard while it is open — rc-menu binds
               * arrow keys and character type-ahead for row navigation, which
               * would swallow what is typed here. Clicks are stopped for the
               * same reason: an unstopped one reaches the menu as a row
               * activation.
               */
              onKeyDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
            />
          </div>
        ),
        children: siteMenuRows,
      },
      {
        key: RESET_GROUP_KEY,
        type: 'group' as const,
        // NOTE: the reset lives on the group label, not on a child item — antd
        // fires `onClick` for leaf items only, so a child would never reach it.
        label: (
          <div
            role='button'
            data-testid='siteRecipientsSiteReset'
            className={twMerge(
              'border-brightGray text-1424 text-spanishGray mt-1 cursor-pointer border-t pt-1 text-center font-semibold',
              !hasSelection && 'cursor-default opacity-50'
            )}
            onClick={() => {
              if (hasSelection) {
                onSelectedSiteChange(undefined);
                closeMenu();
              }
            }}
          >
            Reset to default
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteMenuRows, siteSearch, hasSelection]
  );

  const onMenuClick = (item: MenuInfo) => {
    const key = String(item.key);

    // Only the site rows select anything — the group and hint keys ride the
    // same handler and would otherwise be selected as if they were sites.
    if (!sortedSites.some(site => site.id === key)) return;

    // The picked site closes the menu: with one site at a time there is nothing
    // further to tick, and leaving it open reads as if there were.
    closeMenu();

    if (key === selectedSiteId) return;

    onSelectedSiteChange(key);
  };

  return (
    <div
      role='button'
      /**
       * The chip is only tinted once a site is actually picked — an always-blue
       * chip reads as an active filter before the user has touched it. Matches
       * the sibling Event Filter chip's `bg-body hover:bg-waterLight`.
       */
      className={twMerge(
        'flex h-5 flex-shrink-0 cursor-pointer items-center rounded-md mt-1',
        hasSelection ? 'bg-waterLight' : 'bg-body hover:bg-waterLight'
      )}
    >
      <Dropdown
        onClick={onMenuClick}
        items={menuItems}
        trigger={['click']}
        open={visible}
        disabled={disabled}
        overlayClassName={twMerge('w-[26.4rem]', SITES_SCROLL_CLASS)}
        className='h-full pl-1.5 pr-2.5'
        onOpenChange={(open, { source }) => {
          // 'menu' fires for clicks inside the overlay — the search box lives
          // there, so only the trigger and outside clicks may close it.
          if (source !== 'trigger') return;

          if (open) {
            setVisible(true);

            return;
          }

          closeMenu();
        }}
      >
        <div
          data-testid='siteRecipientsSite'
          className='flex items-center justify-center gap-1'
        >
          <span className='relative block size-[20px]'>
            <Filter height={20} width={20} />

            {hasSelection && (
              <span className='border-waterLight bg-vividTangelo absolute left-[11.5px] top-[0.5px] size-[8px] rounded-full border' />
            )}
          </span>

          <div className='text-1420 text-black whitespace-nowrap'>
            {selectedSiteCode ?? 'Sites'}
          </div>
        </div>
      </Dropdown>
    </div>
  );
};
